import { Fragment } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { mergeFieldDef as mergeFieldDefRaw } from '@platform/shared-types';
import type { BlockType, DocumentBlock, MergeFieldDef } from '@platform/shared-types';
import { chipInfoForDef, type MergeFieldChipInfo } from './mergeFieldChip';
import { IconAlert, IconInfo, IconOctagon, type IconProps } from './ui';
import { CALLOUT_TONE_STYLES } from './calloutTones';

/**
 * Read-only rendering of document blocks — the same output the editor previews,
 * the diff columns show and the public page will eventually serve.
 *
 * See the PALETTE RULE in ./ui.tsx before adding a class.
 */

/** Narrow one member out of the block union, e.g. `BlockOf<'heading'>`. */
export type BlockOf<K extends BlockType> = Extract<DocumentBlock, { type: K }>;

/** Human labels for the add-block menu. `Record<BlockType, …>` on purpose: add a
 *  block type to shared-types and this stops compiling until it is labelled. */
export const BLOCK_TYPE_LABELS: Record<BlockType, string> = {
  heading: 'Heading',
  paragraph: 'Paragraph',
  list: 'List',
  table: 'Table',
  callout: 'Callout',
  mergeField: 'Merge field',
  signatureField: 'Signature field',
  pageBreak: 'Page break',
};

/** The order the add-block menu offers, matching the mockup's footer strip. */
export const BLOCK_TYPE_ORDER: readonly BlockType[] = [
  'heading', 'paragraph', 'list', 'table',
  'callout', 'mergeField', 'signatureField', 'pageBreak',
];

/**
 * `mergeFieldDef` is a RUNTIME export of `@platform/shared-types`, guarded at
 * MODULE SCOPE exactly the way `MergeFieldPalette.tsx` guards
 * `MERGE_FIELD_GROUPS`/`MERGE_FIELD_GROUP_LABELS`/`mergeFieldsByGroup`: a
 * stale Vite pre-bundle can resolve this import to `undefined`, and this is
 * the READ view — the one a non-technical reader opens by default — so it
 * must degrade to the raw `{{token}}` in the danger style rather than
 * white-screening a legal document. This has broken this app three times.
 */
const STALE_BUNDLE_MSG =
  '@platform/shared-types exported no mergeFieldDef — a stale Vite pre-bundle. RESTART the dev server: deleting node_modules/.vite under a running server does nothing.';

const safeMergeFieldDef: (name: string) => MergeFieldDef | undefined = (() => {
  if (typeof mergeFieldDefRaw !== 'function') {
    console.error(STALE_BUNDLE_MSG);
    return () => undefined;
  }
  return (name: string) => {
    try {
      return mergeFieldDefRaw(name);
    } catch (e) {
      console.error('[BlockView] mergeFieldDef() threw:', e);
      return undefined;
    }
  };
})();

/** Resolve `name` (the inside of a `{{name}}` token) to what its chip shows —
 *  the same group-coloured, plain-English chip edit mode uses. All the actual
 *  decision logic is `chipInfoForDef`, in `./mergeFieldChip.ts`, pure and
 *  unit-tested there. */
function resolveMergeFieldChip(name: string): MergeFieldChipInfo {
  return chipInfoForDef(safeMergeFieldDef(name), name);
}

/** The short monospace badge on the left of every editor row (H1 · P · UL · …). */
export function blockTypeBadge(block: DocumentBlock): string {
  switch (block.type) {
    case 'heading': return `H${block.level}`;
    case 'paragraph': return 'P';
    case 'list': return block.ordered ? 'OL' : 'UL';
    case 'table': return 'TBL';
    case 'callout': return '!';
    case 'mergeField': return '{ }';
    case 'signatureField': return 'SIG';
    case 'pageBreak': return 'PB';
    default: return '?';
  }
}

/** A one-line human summary of a block, for timelines and collapsed rows. */
export function blockSummary(block: DocumentBlock): string {
  switch (block.type) {
    case 'heading':
    case 'paragraph':
    case 'callout': return block.text;
    case 'list': return block.items.join(' · ');
    case 'table': return block.headers.join(' | ');
    case 'mergeField': return `{{${block.field}}}`;
    case 'signatureField': return `Signature — ${block.signerRole}`;
    case 'pageBreak': return 'Page break';
    default: return '';
  }
}

const MERGE_FIELD_RE = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;

/**
 * Render `text` with `{{mergeField}}` tokens as the SAME group-coloured,
 * plain-English chip `MergeTextEditor` shows in edit mode — resolved through
 * `resolveMergeFieldChip` so the two modes can never drift into two
 * vocabularies for one field again (this used to render the raw
 * `{{company_legal_name}}` token in a plain blue monospace span here, while
 * edit mode already showed "Company legal name"). The raw token and a sample
 * value sit in the chip's `title` tooltip for a reader who wants to confirm
 * exactly what will be substituted. An unknown/uncatalogued field still
 * renders — in the danger/red style, showing its raw token — never silently
 * vanishing or throwing; see `resolveMergeFieldChip`'s own doc comment for
 * why and for the module-scope catalog-load guard behind it.
 *
 * Plain string splitting — no markdown parser, no `dangerouslySetInnerHTML`,
 * so authored text can never inject markup.
 */
export function renderRichText(text: string): ReactNode {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  // A fresh regex per call: /g regexes carry lastIndex between calls.
  const re = new RegExp(MERGE_FIELD_RE.source, 'g');
  let m = re.exec(text);
  while (m !== null) {
    if (m.index > last) out.push(<Fragment key={key++}>{text.slice(last, m.index)}</Fragment>);
    const chip = resolveMergeFieldChip(m[1]);
    out.push(
      <span
        key={key++}
        title={chip.title}
        className={`rounded px-1.5 py-0.5 font-mono text-[11.5px] ${chip.chipClass}`}
      >
        {chip.label}
      </span>,
    );
    last = m.index + m[0].length;
    m = re.exec(text);
  }
  if (last < text.length) out.push(<Fragment key={key++}>{text.slice(last)}</Fragment>);
  return out;
}

/**
 * The ONE definition of what a callout tone looks like — class, icon and
 * label — consumed by both `BlockView` (read mode, below) and `BlockEditor`
 * (edit mode), so the two can never again drift the way the merge-field chip
 * did before `resolveMergeFieldChip` unified it. See `renderRichText`'s doc
 * comment above for that history.
 *
 * The class/label half of this lives in `./calloutTones.ts`, kept free of the
 * `react` import so it can be unit-tested without a React runtime (this
 * package has no package.json — see `index.ts`'s doc comment, a test run
 * from inside this package alone cannot resolve `react`). This file pairs
 * each tone with its icon component to build the version everything renders
 * from. Colour alone is not how a tone reads: `Icon` and `label` are the
 * colour-blind-safe channel, so danger/warning/info are still distinguishable
 * with no colour at all — shape (octagon/triangle/circle) plus the word.
 */
export const CALLOUT_TONES: Record<
  BlockOf<'callout'>['tone'],
  { className: string; label: string; Icon: ComponentType<IconProps> }
> = {
  info: { ...CALLOUT_TONE_STYLES.info, Icon: IconInfo },
  warning: { ...CALLOUT_TONE_STYLES.warning, Icon: IconAlert },
  danger: { ...CALLOUT_TONE_STYLES.danger, Icon: IconOctagon },
};

export interface BlockViewProps {
  block: DocumentBlock;
  /** Strike the content through — a block that exists in prev and not in next. */
  struck?: boolean;
  className?: string;
}

/** Read-only render of a single block. */
export function BlockView({ block, struck = false, className = '' }: BlockViewProps) {
  const strike = struck ? 'line-through' : '';

  switch (block.type) {
    case 'heading': {
      const size =
        block.level === 1 ? 'text-[17px]' : block.level === 2 ? 'text-[15px]' : 'text-[13.5px]';
      return (
        <div className={`${size} font-semibold text-stone-900 ${strike} ${className}`}>
          {renderRichText(block.text)}
        </div>
      );
    }

    case 'paragraph':
      return (
        <p className={`text-[13px] leading-relaxed text-stone-800 ${strike} ${className}`}>
          {renderRichText(block.text)}
        </p>
      );

    case 'list': {
      // Items have no ids of their own; index is the only stable handle.
      const items = block.items.map((item, i) => <li key={i}>{renderRichText(item)}</li>);
      const listClass = `ml-4 list-outside space-y-1 text-[13px] leading-relaxed text-stone-800 ${
        block.ordered ? 'list-decimal' : 'list-disc'
      } ${strike} ${className}`;
      // Two explicit branches rather than a dynamic tag name: a `'ol' | 'ul'`
      // union in JSX position does not typecheck cleanly across TS versions.
      return block.ordered
        ? <ol className={listClass}>{items}</ol>
        : <ul className={listClass}>{items}</ul>;
    }

    case 'table':
      return (
        <div className={`overflow-x-auto ${strike} ${className}`}>
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr>
                {block.headers.map((h, i) => (
                  <th
                    key={i}
                    className="border border-dark-700 bg-white px-2.5 py-2 text-left font-semibold text-stone-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className="border border-dark-700 px-2.5 py-2 text-stone-800">
                      {renderRichText(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'callout': {
      const tone = CALLOUT_TONES[block.tone];
      return (
        <div
          className={`flex gap-2 rounded-lg border px-3 py-2.5 text-[12.5px] leading-relaxed ${tone.className} ${strike} ${className}`}
        >
          <tone.Icon size={15} className="mt-0.5 shrink-0" />
          <span>
            <span className="mr-1.5 font-semibold uppercase tracking-wide">{tone.label}:</span>
            {renderRichText(block.text)}
          </span>
        </div>
      );
    }

    case 'mergeField': {
      // Same resolver as `renderRichText` — this standalone block used to
      // hardcode the same raw-token blue chip `renderRichText` did, a second
      // copy of the exact style being fixed there.
      const chip = resolveMergeFieldChip(block.field);
      return (
        <div className={`text-[13px] ${strike} ${className}`}>
          <span
            title={chip.title}
            className={`rounded px-1.5 py-0.5 font-mono text-[11.5px] ${chip.chipClass}`}
          >
            {chip.label}
          </span>
          {block.label && <span className="ml-2 text-stone-400">{block.label}</span>}
        </div>
      );
    }

    case 'signatureField':
      return (
        <div
          className={`rounded-lg border border-dashed border-stone-200 px-3 py-3 text-[12.5px] text-stone-400 ${strike} ${className}`}
        >
          <span className="font-semibold uppercase tracking-wider text-stone-500">Signature</span>
          <span className="ml-2 text-stone-800">{block.label || block.signerRole}</span>
          <span className="ml-2 font-mono text-[11px] text-stone-500">{block.signerRole}</span>
        </div>
      );

    case 'pageBreak':
      return (
        <div className={`flex items-center gap-3 py-1 ${className}`}>
          <span className="h-px flex-1 border-t border-dashed border-stone-200" />
          <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">
            Page break
          </span>
          <span className="h-px flex-1 border-t border-dashed border-stone-200" />
        </div>
      );

    default:
      return null;
  }
}

/** Read-only render of a whole version's blocks — the "Preview" surface. */
export function BlockList({
  blocks, className = '',
}: { blocks: readonly DocumentBlock[]; className?: string }) {
  if (blocks.length === 0) {
    return (
      <p className={`text-[13px] text-stone-500 ${className}`}>
        This version has no blocks yet.
      </p>
    );
  }
  return (
    <div className={`space-y-3 ${className}`}>
      {blocks.map((block) => (
        <BlockView key={block.id} block={block} />
      ))}
    </div>
  );
}
